import React, { useState, useRef } from 'react';
import { Document, Page } from 'react-pdf';
import { pdfjs } from '../utils/pdfConfig';
import '../styles/components/PDFViewer.css';

interface PDFViewerProps {
  file: File | null;
  onClose: () => void;
}

const PDFViewer: React.FC<PDFViewerProps> = ({ file, onClose }) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setFileUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [file]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const goToPrevPage = () => {
    setPageNumber(prev => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber(prev => Math.min(prev + 1, numPages));
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      const url = URL.createObjectURL(selectedFile);
      setFileUrl(url);
    }
  };

  if (!fileUrl) {
    return (
      <div className="pdf-viewer">
        <div className="pdf-controls">
          <button onClick={onClose}>닫기</button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <button onClick={() => fileInputRef.current?.click()}>
            PDF 파일 선택
          </button>
        </div>
        <div className="pdf-placeholder">
          <p>PDF 파일을 선택하거나 드래그하여 업로드하세요</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pdf-viewer">
      <div className="pdf-controls">
        <button onClick={onClose}>닫기</button>
        <button onClick={goToPrevPage} disabled={pageNumber <= 1}>
          이전 페이지
        </button>
        <span>
          {pageNumber} / {numPages}
        </span>
        <button onClick={goToNextPage} disabled={pageNumber >= numPages}>
          다음 페이지
        </button>
        <button onClick={() => setScale(prev => Math.max(prev - 0.1, 0.5))}>
          축소
        </button>
        <span>{Math.round(scale * 100)}%</span>
        <button onClick={() => setScale(prev => Math.min(prev + 0.1, 3.0))}>
          확대
        </button>
      </div>
      
      <div className="pdf-content">
        <Document
          file={fileUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={<div>PDF 로딩 중...</div>}
          error={<div>PDF 로딩 실패</div>}
        >
          <Page
            pageNumber={pageNumber}
            scale={scale}
            renderTextLayer={true}
            renderAnnotationLayer={true}
          />
        </Document>
      </div>
    </div>
  );
};

export default PDFViewer;
